import type * as datePicker from '@zag-js/date-picker'
import { CalendarDate } from '@internationalized/date'
import { addDays, isSameDay, isAfter, isBefore, startOfDay, isWithinInterval, subDays } from 'date-fns'
import type { DateRange, GetDateRangeFromPreset } from './types'
import { CUSTOM_PRESET_VALUE } from './constants'

export const MONTHS_TO_DISPLAY = 12

/** How many extra months are appended each time the user scrolls to an edge of the rendered window. */
export const MONTHS_PER_EXTENSION = 12

export function toCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

export function fromCalendarDate(cal: datePicker.DateValue): Date {
  return new Date(cal.year, cal.month - 1, cal.day)
}

const clampCalendarDate = (date: CalendarDate, min?: CalendarDate, max?: CalendarDate): CalendarDate => {
  if (min != null && date.compare(min) < 0) return min
  if (max != null && date.compare(max) > 0) return max
  return date
}

/**
 * Clamp a `DateRange` to optional [minDate, maxDate], matching Zag's calendar-date
 * clamp so preset resolvers (e.g. `allTime` from 2000) cannot surface out-of-bounds
 * dates when the picker is bounded.
 */
export function clampDateRange(range: DateRange, minDate?: Date, maxDate?: Date): DateRange {
  const min = minDate != null ? toCalendarDate(minDate) : undefined
  const max = maxDate != null ? toCalendarDate(maxDate) : undefined
  return {
    from: fromCalendarDate(clampCalendarDate(toCalendarDate(range.from), min, max)),
    to: range.to != null ? fromCalendarDate(clampCalendarDate(toCalendarDate(range.to), min, max)) : undefined
  }
}

/**
 * How far either side of a preset date we look for a selectable day. Needed because an
 * unbounded picker (no `minDate`/`maxDate`) would otherwise search forever.
 */
const SELECTABLE_DATE_SEARCH_LIMIT_DAYS = 366

/**
 * Nearest day to `date` that is selectable, i.e. inside [minDate, maxDate] and not rejected by
 * `isDateUnavailable`. Ties go to the earlier day, matching how a single-date preset reads as
 * "up to this day". Returns `null` when nothing nearby qualifies.
 */
export function findSelectableDate(
  date: Date,
  isDateUnavailable?: (date: Date) => boolean,
  minDate?: Date,
  maxDate?: Date
): Date | null {
  const day = startOfDay(date)
  if (isDateOutsideBounds(day, minDate, maxDate)) return null
  if (isDateUnavailable?.(day) !== true) return day

  for (let offset = 1; offset <= SELECTABLE_DATE_SEARCH_LIMIT_DAYS; offset++) {
    for (const candidate of [subDays(day, offset), addDays(day, offset)]) {
      if (!isDateOutsideBounds(candidate, minDate, maxDate) && !isDateUnavailable(candidate)) return candidate
    }
  }
  return null
}

/**
 * Whether Zag already holds exactly `next`. Its value store skips `onValueChange`
 * for an equal value, and it clamps incoming dates to [min, max] before storing
 * them, so the comparison has to clamp too.
 */
export function holdsSameValue(
  current: readonly datePicker.DateValue[],
  next: readonly CalendarDate[],
  minDate?: Date,
  maxDate?: Date
): boolean {
  if (current.length !== next.length) return false
  const min = minDate != null ? toCalendarDate(minDate) : undefined
  const max = maxDate != null ? toCalendarDate(maxDate) : undefined
  return next.every((date, index) => clampCalendarDate(date, min, max).compare(current[index]) === 0)
}

export function resolveRangeSelection(
  selectedRange: DateRange | null,
  selectedPreset: string | null | undefined,
  getDateRangeFromPreset?: GetDateRangeFromPreset,
  maxDate?: Date,
  minDate?: Date
): { range: DateRange | null; presetValue: string } {
  if (selectedPreset == null || selectedPreset === CUSTOM_PRESET_VALUE || getDateRangeFromPreset == null) {
    return { range: selectedRange, presetValue: CUSTOM_PRESET_VALUE }
  }

  const range = getDateRangeFromPreset(selectedPreset, maxDate)
  if (range == null) {
    return { range: selectedRange, presetValue: CUSTOM_PRESET_VALUE }
  }

  return { range: clampDateRange(range, minDate, maxDate), presetValue: selectedPreset }
}

export function getStartMonth(minDate?: Date): Date {
  const ref = minDate ?? new Date()
  return new Date(ref.getFullYear(), ref.getMonth(), 1)
}

export function buildMonthList(startMonth: Date, count: number = MONTHS_TO_DISPLAY): Date[] {
  return Array.from({ length: count }, (_, i) => new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1))
}

const firstOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1)
const monthDiff = (a: Date, b: Date): number => (a.getFullYear() - b.getFullYear()) * 12 + (a.getMonth() - b.getMonth())
const addMonths = (date: Date, n: number): Date => new Date(date.getFullYear(), date.getMonth() + n, 1)

/** Clamp a first-of-month date into the optional [minMonth, maxMonth] range. */
const clampMonth = (month: Date, minMonth: Date | null, maxMonth: Date | null): Date => {
  if (minMonth != null && month < minMonth) return minMonth
  if (maxMonth != null && month > maxMonth) return maxMonth
  return month
}

/** Grow [start, end] outward so every must-include month falls inside the window. */
const stretchToInclude = (start: Date, end: Date, dates: Array<Date | undefined>): [Date, Date] => {
  for (const date of dates) {
    if (date == null) continue
    const m = firstOfMonth(date)
    if (m < start) start = m
    if (m > end) end = m
  }
  return [start, end]
}

/** Inclusive list of first-of-month dates from `start` to `end`. */
const buildMonthRange = (start: Date, end: Date): Date[] => {
  const months: Date[] = []
  for (let m = start; m <= end; m = addMonths(m, 1)) months.push(m)
  return months
}

export interface MonthWindow {
  /** First-of-month dates rendered in the calendar, oldest first. */
  months: Date[]
  /** Index of the anchor month within {@link months}. */
  anchorIndex: number
}

/**
 * Build a month window centred on `anchor` (≈ count/2 months before, the rest after),
 * clamped to [minDate, maxDate]. Returns the months and the index of the anchor in the
 * resulting array (so the caller can scroll it into view).
 *
 * Any dates in `mustInclude` are guaranteed to fall inside the rendered window —
 * this is how a far-off comparison range (e.g. the previous-year period, which
 * sits ~12 months before the main range) stays rendered so it can be highlighted.
 *
 * When both bounds bite, the window may be shorter than `count`.
 */
export function resolveMonthWindow(
  anchor: Date,
  minDate?: Date,
  maxDate?: Date,
  count: number = MONTHS_TO_DISPLAY,
  mustInclude: Array<Date | undefined> = []
): MonthWindow {
  const minMonth = minDate == null ? null : firstOfMonth(minDate)
  const maxMonth = maxDate == null ? null : firstOfMonth(maxDate)

  const anchorMonth = clampMonth(firstOfMonth(anchor), minMonth, maxMonth)

  const before = Math.floor(count / 2)
  let start = addMonths(anchorMonth, -before)

  // If the tail would exceed maxMonth, slide the window back so it ends on maxMonth.
  if (maxMonth != null && addMonths(start, count - 1) > maxMonth) {
    start = addMonths(maxMonth, -(count - 1))
  }

  // Clamp the head; this can shrink the window when both bounds bite.
  if (minMonth != null && start < minMonth) start = minMonth

  let end = addMonths(start, count - 1)

  // Stretch the window so every must-include month stays rendered.
  ;[start, end] = stretchToInclude(start, end, mustInclude)

  // Re-apply bounds after the stretch so we never render outside [minDate, maxDate].
  if (minMonth != null && start < minMonth) start = minMonth
  if (maxMonth != null && end > maxMonth) end = maxMonth

  const months = buildMonthRange(start, end)
  const anchorIndex = monthDiff(anchorMonth, start)
  return { months, anchorIndex }
}

/**
 * Grow a resolved window outward by whole months, clamped to [minDate, maxDate].
 *
 * The calendar is scroll-only, so the rendered window is the effective selectable
 * range: a month that is never rendered can never be clicked. Growing the window
 * as the user scrolls is what lets them reach dates arbitrarily far from the anchor.
 */
export function growMonthWindow(
  window: MonthWindow,
  monthsBefore: number,
  monthsAfter: number,
  minDate?: Date,
  maxDate?: Date
): MonthWindow {
  const first = window.months[0]
  const last = window.months.at(-1)
  const anchorMonth = window.months[window.anchorIndex]
  if (first == null || last == null || anchorMonth == null) return window
  if (monthsBefore <= 0 && monthsAfter <= 0) return window

  let start = addMonths(first, -Math.max(monthsBefore, 0))
  let end = addMonths(last, Math.max(monthsAfter, 0))

  const minMonth = minDate == null ? null : firstOfMonth(minDate)
  const maxMonth = maxDate == null ? null : firstOfMonth(maxDate)
  if (minMonth != null && start < minMonth) start = minMonth
  if (maxMonth != null && end > maxMonth) end = maxMonth

  return { months: buildMonthRange(start, end), anchorIndex: monthDiff(anchorMonth, start) }
}

/** Whether the window can still grow towards the past without breaching `minDate`. */
export function canGrowMonthWindowBackward(window: MonthWindow, minDate?: Date): boolean {
  const first = window.months[0]
  if (first == null) return false
  return minDate == null || firstOfMonth(minDate) < first
}

/** Whether the window can still grow towards the future without breaching `maxDate`. */
export function canGrowMonthWindowForward(window: MonthWindow, maxDate?: Date): boolean {
  const last = window.months.at(-1)
  if (last == null) return false
  return maxDate == null || firstOfMonth(maxDate) > last
}

export function isDateOutsideBounds(date: Date, minDate?: Date, maxDate?: Date): boolean {
  const day = startOfDay(date)
  if (minDate != null && isBefore(day, startOfDay(minDate))) return true
  if (maxDate != null && isAfter(day, startOfDay(maxDate))) return true
  return false
}

export function doRangesOverlap(range1: DateRange, range2: DateRange): boolean {
  const r1End = range1.to ?? range1.from
  const r2End = range2.to ?? range2.from
  return !isBefore(r1End, range2.from) && !isBefore(r2End, range1.from)
}

export function getOverlapPeriod(range1: DateRange, range2: DateRange): DateRange | null {
  if (!doRangesOverlap(range1, range2)) return null

  const r1End = range1.to ?? range1.from
  const r2End = range2.to ?? range2.from

  const overlapStart = isAfter(range1.from, range2.from) ? range1.from : range2.from
  const overlapEnd = isBefore(r1End, r2End) ? r1End : r2End

  return {
    from: overlapStart,
    to: isSameDay(overlapStart, overlapEnd) ? undefined : overlapEnd
  }
}

export function isRangeEndInComparisonRange(
  date: Date,
  selectedRange: DateRange | null,
  comparisonRange: DateRange | null
): boolean {
  if (selectedRange?.to == null || comparisonRange == null) return false
  const rangeEnd = selectedRange.to
  if (!isSameDay(date, rangeEnd)) return false
  return isWithinInterval(rangeEnd, {
    start: comparisonRange.from,
    end: comparisonRange.to ?? comparisonRange.from
  })
}
